// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const lineItemMetricsDailyCubeMeta = {
  name: 'line_item_metrics_daily',
  sqlTable: 'metrics.line_item_metrics_daily',
  title: 'Line Item Metrics Daily',
  description: 'Daily grain metrics per line item. Use for time-series.',
  joins: {
    line_items: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry,
    campaigns: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'line_item_metrics_daily.count': {
      type: 'number',
      aggType: 'count',
      title: 'Record Count',
      description: 'Number of daily metric records',
      meta: {
        category: 'utility',
        grain: 'daily',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Row count, not a business metric',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_spend': {
      sql: 'planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Spend',
      format: 'currency',
      description: 'Daily planned media spend',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_spend)',
        inputs: [],
        short_description: 'Daily planned media spend from the pacing plan',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_units': {
      sql: 'planned_units',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Units',
      format: 'number',
      description: 'Daily planned units to deliver',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_units)',
        inputs: [],
        short_description: 'Daily planned units to deliver from the pacing plan',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_daily_spend': {
      sql: 'planned_spend',
      type: 'number',
      aggType: 'avg',
      title: 'Planned Daily Spend',
      format: 'currency',
      description: 'Average planned spend per day over the queried date range',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: false,
        formula: 'AVG(planned_spend)',
        inputs: [],
        short_description: 'Average planned daily spend across queried range',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_daily_units': {
      sql: 'planned_units',
      type: 'number',
      aggType: 'avg',
      title: 'Planned Daily Units',
      format: 'number',
      description: 'Average planned units per day over the queried date range',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: false,
        formula: 'AVG(planned_units)',
        inputs: [],
        short_description: 'Average planned daily units across queried range',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_revenue': {
      sql: 'planned_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Revenue',
      format: 'currency',
      description: 'Daily planned revenue',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_revenue)',
        inputs: [],
        short_description: 'Daily planned revenue (gross, before referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_spend': {
      sql: 'actual_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Spend',
      format: 'currency',
      description: 'Daily actual media spend from platform data',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(actual_spend)',
        inputs: [],
        short_description: 'Daily actual media spend from platform data',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.remaining_spend': {
      sql: 'line_item_metrics_daily.planned_spend - line_item_metrics_daily.actual_spend',
      type: 'number',
      aggType: 'number',
      title: 'Remaining Spend',
      format: 'currency',
      description:
        'Planned spend minus actual spend over specified date range. Negative values indicate overspend.',
      meta: {
        category: 'spend',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'planned_spend - actual_spend',
        inputs: ['line_item_metrics_daily.planned_spend', 'line_item_metrics_daily.actual_spend'],
        short_description: 'Remaining spend (planned minus actual)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_units': {
      sql: 'actual_units',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Units',
      format: 'number',
      description: 'Daily actual units delivered from platform data',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(actual_units)',
        inputs: [],
        short_description: 'Daily actual units delivered from platform data',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_units_last_3_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '3 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.actual_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Total for last 3 days',
      format: 'number',
      description: 'Total actual units for past 3 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '3_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(actual_units) where day >= current_date - interval '3 day' and day < current_date",
        inputs: [],
        short_description: 'Total actual units for past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_units_last_7_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '7 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.actual_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Total for last 7 days',
      format: 'number',
      description: 'Total actual units for past 7 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '7_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(actual_units) where day >= current_date - interval '7 day' and day < current_date",
        inputs: [],
        short_description: 'Total actual units for past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_units_yesterday': {
      sql: "case when line_item_metrics_daily.day = current_date - interval '1 day' then coalesce(line_item_metrics_daily.actual_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: "Yesterday's units",
      format: 'number',
      description: 'Actual units delivered yesterday',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '1_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: "SUM(actual_units) where day = current_date - interval '1 day'",
        inputs: [],
        short_description: 'Actual units for yesterday - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_units_yesterday': {
      sql: "case when line_item_metrics_daily.day = current_date - interval '1 day' then coalesce(line_item_metrics_daily.planned_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: "Yesterday's planned units",
      format: 'number',
      description: 'Planned units for yesterday from the pacing schedule',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '1_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: "SUM(planned_units) where day = current_date - interval '1 day'",
        inputs: [],
        short_description: 'Planned units for yesterday - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_actual_units_last_3_days': {
      sql: 'line_item_metrics_daily.actual_units_last_3_days / 3.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily units (last 3 days)',
      format: 'number',
      description: 'Average daily actual units over the past 3 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '3_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'actual_units_last_3_days / 3',
        inputs: ['line_item_metrics_daily.actual_units_last_3_days'],
        short_description:
          'Average daily actual units over past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_actual_units_last_7_days': {
      sql: 'line_item_metrics_daily.actual_units_last_7_days / 7.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily units (last 7 days)',
      format: 'number',
      description: 'Average daily actual units over the past 7 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '7_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'actual_units_last_7_days / 7',
        inputs: ['line_item_metrics_daily.actual_units_last_7_days'],
        short_description:
          'Average daily actual units over past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_units_last_3_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '3 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.planned_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Planned total for last 3 days',
      format: 'number',
      description: 'Total planned units for past 3 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '3_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(planned_units) where day >= current_date - interval '3 day' and day < current_date",
        inputs: [],
        short_description:
          'Total planned units for past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_units_last_7_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '7 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.planned_units, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Planned total for last 7 days',
      format: 'number',
      description: 'Total planned units for past 7 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '7_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(planned_units) where day >= current_date - interval '7 day' and day < current_date",
        inputs: [],
        short_description:
          'Total planned units for past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_planned_units_last_3_days': {
      sql: 'line_item_metrics_daily.planned_units_last_3_days / 3.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily planned units (last 3 days)',
      format: 'number',
      description: 'Average daily planned units over the past 3 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '3_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'planned_units_last_3_days / 3',
        inputs: ['line_item_metrics_daily.planned_units_last_3_days'],
        short_description:
          'Average daily planned units over past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_planned_units_last_7_days': {
      sql: 'line_item_metrics_daily.planned_units_last_7_days / 7.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily planned units (last 7 days)',
      format: 'number',
      description: 'Average daily planned units over the past 7 days',
      meta: {
        category: 'delivery',
        grain: 'multi_day',
        period: '7_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'planned_units_last_7_days / 7',
        inputs: ['line_item_metrics_daily.planned_units_last_7_days'],
        short_description:
          'Average daily planned units over past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_spend_yesterday': {
      sql: "case when line_item_metrics_daily.day = current_date - interval '1 day' then coalesce(line_item_metrics_daily.actual_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: "Yesterday's spend",
      format: 'currency',
      description: 'Actual spend yesterday',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '1_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: "SUM(actual_spend) where day = current_date - interval '1 day'",
        inputs: [],
        short_description: 'Actual spend for yesterday - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_spend_yesterday': {
      sql: "case when line_item_metrics_daily.day = current_date - interval '1 day' then coalesce(line_item_metrics_daily.planned_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: "Yesterday's planned spend",
      format: 'currency',
      description: 'Planned spend for yesterday from the pacing schedule',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '1_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: "SUM(planned_spend) where day = current_date - interval '1 day'",
        inputs: [],
        short_description: 'Planned spend for yesterday - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_spend_last_3_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '3 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.actual_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Spend total for last 3 days',
      format: 'currency',
      description: 'Total actual spend for past 3 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '3_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(actual_spend) where day >= current_date - interval '3 day' and day < current_date",
        inputs: [],
        short_description: 'Total actual spend for past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_spend_last_3_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '3 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.planned_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Planned spend total for last 3 days',
      format: 'currency',
      description: 'Total planned spend for past 3 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '3_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(planned_spend) where day >= current_date - interval '3 day' and day < current_date",
        inputs: [],
        short_description:
          'Total planned spend for past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.actual_spend_last_7_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '7 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.actual_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Spend total for last 7 days',
      format: 'currency',
      description: 'Total actual spend for past 7 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '7_day',
        type: 'actual',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(actual_spend) where day >= current_date - interval '7 day' and day < current_date",
        inputs: [],
        short_description: 'Total actual spend for past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.planned_spend_last_7_days': {
      sql: "case when line_item_metrics_daily.day >= current_date - interval '7 day' and line_item_metrics_daily.day < current_date then coalesce(line_item_metrics_daily.planned_spend, 0) else 0 end",
      type: 'number',
      aggType: 'sum',
      title: 'Planned spend total for last 7 days',
      format: 'currency',
      description: 'Total planned spend for past 7 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '7_day',
        type: 'planned',
        supports_rollup: false,
        supports_day_dimension: false,
        formula:
          "SUM(planned_spend) where day >= current_date - interval '7 day' and day < current_date",
        inputs: [],
        short_description:
          'Total planned spend for past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_actual_spend_last_3_days': {
      sql: 'line_item_metrics_daily.actual_spend_last_3_days / 3.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily spend (last 3 days)',
      format: 'currency',
      description: 'Average daily actual spend over the past 3 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '3_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'actual_spend_last_3_days / 3',
        inputs: ['line_item_metrics_daily.actual_spend_last_3_days'],
        short_description:
          'Average daily actual spend over past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_planned_spend_last_3_days': {
      sql: 'line_item_metrics_daily.planned_spend_last_3_days / 3.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily planned spend (last 3 days)',
      format: 'currency',
      description: 'Average daily planned spend over the past 3 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '3_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'planned_spend_last_3_days / 3',
        inputs: ['line_item_metrics_daily.planned_spend_last_3_days'],
        short_description:
          'Average daily planned spend over past 3 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_actual_spend_last_7_days': {
      sql: 'line_item_metrics_daily.actual_spend_last_7_days / 7.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily spend (last 7 days)',
      format: 'currency',
      description: 'Average daily actual spend over the past 7 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '7_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'actual_spend_last_7_days / 7',
        inputs: ['line_item_metrics_daily.actual_spend_last_7_days'],
        short_description:
          'Average daily actual spend over past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.avg_planned_spend_last_7_days': {
      sql: 'line_item_metrics_daily.planned_spend_last_7_days / 7.0',
      type: 'number',
      aggType: 'number',
      title: 'Avg daily planned spend (last 7 days)',
      format: 'currency',
      description: 'Average daily planned spend over the past 7 days',
      meta: {
        category: 'spend',
        grain: 'multi_day',
        period: '7_day',
        type: 'derived',
        supports_rollup: false,
        supports_day_dimension: false,
        formula: 'planned_spend_last_7_days / 7',
        inputs: ['line_item_metrics_daily.planned_spend_last_7_days'],
        short_description:
          'Average daily planned spend over past 7 days - NOT VALID when broken down by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.remaining_units': {
      sql: 'line_item_metrics_daily.planned_units - line_item_metrics_daily.actual_units',
      type: 'number',
      aggType: 'number',
      title: 'Remaining Units',
      format: 'number',
      description:
        'Planned units minus actual units over specified date range. Negative values indicate overdelivery.',
      meta: {
        category: 'delivery',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'planned_units - actual_units',
        inputs: ['line_item_metrics_daily.planned_units', 'line_item_metrics_daily.actual_units'],
        short_description: 'Remaining units (planned minus actual)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.uncapped_revenue': {
      sql: 'uncapped_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Revenue',
      format: 'currency',
      description: 'Daily uncapped revenue from platform data (before commission)',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(uncapped_revenue)',
        inputs: [],
        short_description: 'Daily uncapped revenue (gross, before referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.media_cost': {
      sql: 'media_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Media Cost',
      format: 'currency',
      description:
        'Daily media cost (COGS). Equals actual_spend for standard line items but is 0 for management_fee items. This distinction matters for margin calculations: margin = (revenue - media_cost) / revenue.',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(media_cost)',
        inputs: [],
        short_description: 'Daily media cost (COGS). 0 for management_fee items',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.effective_planned_revenue': {
      sql: 'line_item_metrics_daily.planned_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Planned Revenue',
      format: 'currency',
      description:
        'Planned revenue after deducting referral commission. Can aggregate across entities or use in time series.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'effective',
        supports_rollup: true,
        supports_timeseries: true,
        formula: 'planned_revenue * (1 - referral_commission_rate)',
        inputs: ['line_item_metrics_daily.planned_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Daily planned revenue after deducting referral commission',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.uncapped_effective_revenue': {
      sql: 'line_item_metrics_daily.uncapped_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Effective Revenue',
      format: 'currency',
      description:
        'Uncapped revenue after deducting referral commission. Can aggregate across entities or use in time series.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'effective',
        supports_rollup: true,
        supports_timeseries: true,
        formula: 'uncapped_revenue * (1 - referral_commission_rate)',
        inputs: ['line_item_metrics_daily.uncapped_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Daily uncapped revenue after deducting referral commission',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.billable_revenue': {
      sql: 'billable_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Billable Revenue',
      format: 'currency',
      description:
        'Daily billable revenue (capped per rollover setting). Daily increment of capped cumulative revenue.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        supports_timeseries: true,
        formula: 'SUM(billable_revenue)',
        inputs: [],
        short_description: 'Daily billable revenue (capped per rollover setting)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.effective_billable_revenue': {
      sql: 'line_item_metrics_daily.billable_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Billable Revenue',
      format: 'currency',
      description:
        'Daily billable revenue after deducting referral commission. Use for margin calculations.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'effective',
        supports_rollup: true,
        supports_timeseries: true,
        formula: 'billable_revenue * (1 - referral_commission_rate)',
        inputs: ['line_item_metrics_daily.billable_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Daily billable revenue after deducting referral commission',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_planned_revenue': {
      sql: 'cumulative_planned_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Planned Revenue',
      format: 'currency',
      description: 'Running total of planned revenue through this day',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'cumulative',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_planned_revenue)',
        inputs: [],
        short_description: 'Running total of planned revenue through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_uncapped_revenue': {
      sql: 'cumulative_uncapped_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Uncapped Revenue',
      format: 'currency',
      description: 'Running total of uncapped revenue through this day',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'cumulative',
        type: 'actual',
        supports_rollup: false,
        formula: 'MAX(cumulative_uncapped_revenue)',
        inputs: [],
        short_description: 'Running total of uncapped revenue through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_planned_units': {
      sql: 'cumulative_planned_units',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Planned Units',
      format: 'number',
      description: 'Running total of planned units through this day',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'cumulative',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_planned_units)',
        inputs: [],
        short_description: 'Running total of planned units through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_actual_units': {
      sql: 'cumulative_actual_units',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Actual Units',
      format: 'number',
      description: 'Running total of actual units through this day',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'cumulative',
        type: 'actual',
        supports_rollup: false,
        formula: 'MAX(cumulative_actual_units)',
        inputs: [],
        short_description: 'Running total of actual units through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_planned_spend': {
      sql: 'line_item_metrics_daily.cumulative_planned_units * line_item_metrics_daily.line_items.planned_unit_cost',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Planned Spend',
      format: 'currency',
      description:
        'Running total of planned spend through this day (cumulative_planned_units * planned_unit_cost)',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'cumulative',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_planned_units * planned_unit_cost)',
        inputs: [
          'line_item_metrics_daily.cumulative_planned_units',
          'line_items.planned_unit_cost'
        ],
        short_description: 'Running total of planned spend through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_media_cost': {
      sql: 'cumulative_media_cost',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Media Cost',
      format: 'currency',
      description: 'Running total of media cost through this day',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'cumulative',
        type: 'actual',
        supports_rollup: false,
        formula: 'MAX(cumulative_media_cost)',
        inputs: [],
        short_description: 'Running total of media cost through this day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_effective_planned_revenue': {
      sql: 'line_item_metrics_daily.cumulative_planned_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Effective Planned Revenue',
      format: 'currency',
      description:
        'Running total of effective planned revenue. For time series only - requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'cumulative',
        type: 'effective',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'cumulative_planned_revenue * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_planned_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Running total of effective planned revenue (after referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_effective_uncapped_revenue': {
      sql: 'line_item_metrics_daily.cumulative_uncapped_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Effective Uncapped Revenue',
      format: 'currency',
      description:
        'Running total of effective uncapped revenue. For time series only - requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'cumulative',
        type: 'effective',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'cumulative_uncapped_revenue * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_uncapped_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description:
          'Running total of effective uncapped revenue (after referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_planned_revenue': {
      sql: 'cumulative_monthly_planned_revenue',
      type: 'number',
      aggType: 'max',
      title: 'MTD Planned Revenue',
      format: 'currency',
      description: 'Month-to-date planned revenue. Resets at month boundary.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'mtd',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_monthly_planned_revenue)',
        inputs: [],
        short_description: 'Month-to-date planned revenue. Resets at month boundary',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_uncapped_revenue': {
      sql: 'cumulative_monthly_uncapped_revenue',
      type: 'number',
      aggType: 'max',
      title: 'MTD Uncapped Revenue',
      format: 'currency',
      description: 'Month-to-date uncapped revenue. Resets at month boundary.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'mtd',
        type: 'actual',
        supports_rollup: false,
        formula: 'MAX(cumulative_monthly_uncapped_revenue)',
        inputs: [],
        short_description: 'Month-to-date uncapped revenue. Resets at month boundary',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_planned_units': {
      sql: 'cumulative_monthly_planned_units',
      type: 'number',
      aggType: 'max',
      title: 'MTD Planned Units',
      format: 'number',
      description: 'Month-to-date planned units. Resets at month boundary.',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'mtd',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_monthly_planned_units)',
        inputs: [],
        short_description: 'Month-to-date planned units. Resets at month boundary',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_actual_units': {
      sql: 'cumulative_monthly_actual_units',
      type: 'number',
      aggType: 'max',
      title: 'MTD Actual Units',
      format: 'number',
      description: 'Month-to-date actual units. Resets at month boundary.',
      meta: {
        category: 'delivery',
        grain: 'daily',
        period: 'mtd',
        type: 'actual',
        supports_rollup: false,
        formula: 'MAX(cumulative_monthly_actual_units)',
        inputs: [],
        short_description: 'Month-to-date actual units. Resets at month boundary',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_effective_planned_revenue': {
      sql: 'line_item_metrics_daily.cumulative_monthly_planned_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'MTD Effective Planned Revenue',
      format: 'currency',
      description:
        'Month-to-date effective planned revenue. For time series only - requires line_item_id and day grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'mtd',
        type: 'effective',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: [
          'line_item_metrics_daily.line_item_id',
          'line_item_metrics_daily.day'
        ],
        formula: 'cumulative_monthly_planned_revenue * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_monthly_planned_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Month-to-date effective planned revenue (after referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.cumulative_monthly_effective_uncapped_revenue': {
      sql: 'line_item_metrics_daily.cumulative_monthly_uncapped_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'MTD Effective Uncapped Revenue',
      format: 'currency',
      description:
        'Month-to-date effective uncapped revenue. For time series only - requires line_item_id and day grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'mtd',
        type: 'effective',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: [
          'line_item_metrics_daily.line_item_id',
          'line_item_metrics_daily.day'
        ],
        formula: 'cumulative_monthly_uncapped_revenue * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_monthly_uncapped_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Month-to-date effective uncapped revenue (after referral commission)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.capped_monthly_revenue': {
      sql: 'capped_monthly_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Monthly Revenue Cap',
      format: 'currency',
      description:
        'Monthly revenue cap for budget-capped line items. Same value for all days in a month.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'cap',
        supports_rollup: false,
        formula: 'MAX(capped_monthly_revenue)',
        inputs: [],
        short_description:
          'Monthly revenue cap for budget-capped line items. Same value for all days in a month',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.effective_capped_monthly_revenue': {
      sql: 'line_item_metrics_daily.capped_monthly_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Effective Monthly Revenue Cap',
      format: 'currency',
      description: 'Monthly revenue cap after deducting referral commission.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'effective_cap',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'capped_monthly_revenue * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.capped_monthly_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Monthly revenue cap after deducting referral commission',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.current_capped_monthly_revenue': {
      sql: "MAX(CASE WHEN date_trunc('month', line_item_metrics_daily.day) = date_trunc('month', CURRENT_DATE) THEN line_item_metrics_daily.capped_monthly_revenue END)",
      type: 'number',
      aggType: 'number',
      title: 'Current Month Revenue Cap',
      format: 'currency',
      description: 'Monthly revenue cap for current month. Requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'cap',
        supports_rollup: false,
        supports_timeseries: false,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'MAX(capped_monthly_revenue) WHERE month = current month',
        inputs: ['line_item_metrics_daily.capped_monthly_revenue'],
        short_description: 'Monthly revenue cap for current month only',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.current_effective_capped_monthly_revenue': {
      sql: "MAX(CASE WHEN date_trunc('month', line_item_metrics_daily.day) = date_trunc('month', CURRENT_DATE) THEN line_item_metrics_daily.capped_monthly_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0)) END)",
      type: 'number',
      aggType: 'number',
      title: 'Current Month Effective Revenue Cap',
      format: 'currency',
      description: 'Effective monthly revenue cap for current month after referral commission.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'effective_cap',
        supports_rollup: false,
        supports_timeseries: false,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula:
          'MAX(capped_monthly_revenue * (1 - referral_commission_rate)) WHERE month = current month',
        inputs: [
          'line_item_metrics_daily.capped_monthly_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description:
          'Effective monthly revenue cap (after referral commission) for current month',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.monthly_capped_actual_revenue': {
      sql: 'LEAST(line_item_metrics_daily.cumulative_monthly_uncapped_revenue, line_item_metrics_daily.capped_monthly_revenue)',
      type: 'number',
      aggType: 'max',
      title: 'Monthly Capped Revenue',
      format: 'currency',
      description:
        "Uncapped revenue capped to the month's planned total. MIN(cumulative_monthly_uncapped, monthly_cap). Requires line_item_id grouping.",
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'LEAST(cumulative_monthly_uncapped_revenue, capped_monthly_revenue)',
        inputs: [
          'line_item_metrics_daily.cumulative_monthly_uncapped_revenue',
          'line_item_metrics_daily.capped_monthly_revenue'
        ],
        short_description:
          'Uncapped revenue capped at monthly revenue cap. Prevents overdelivery from inflating reported revenue',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.effective_monthly_capped_actual_revenue': {
      sql: 'LEAST(line_item_metrics_daily.cumulative_monthly_uncapped_revenue, line_item_metrics_daily.capped_monthly_revenue) * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Effective Monthly Capped Revenue',
      format: 'currency',
      description:
        'Monthly capped revenue after deducting referral commission. Requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'effective_capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula:
          'LEAST(cumulative_monthly_uncapped_revenue, capped_monthly_revenue) * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_monthly_uncapped_revenue',
          'line_item_metrics_daily.capped_monthly_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Monthly capped uncapped revenue after deducting referral commission',
        explanation:
          'Takes the lesser of month-to-date uncapped revenue and the monthly revenue cap, then applies the referral commission deduction. Prevents overdelivery from inflating net revenue within a month.',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.lifetime_capped_actual_revenue': {
      sql: 'LEAST(line_item_metrics_daily.cumulative_uncapped_revenue, line_item_metrics_daily.line_items.price_value)',
      type: 'number',
      aggType: 'max',
      title: 'Lifetime Capped Revenue',
      format: 'currency',
      description:
        'Uncapped revenue capped to line item price (lifetime). MIN(cumulative_uncapped, price). Requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'lifetime',
        type: 'capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'LEAST(cumulative_uncapped_revenue, price)',
        inputs: ['line_item_metrics_daily.cumulative_uncapped_revenue', 'line_items.price_value'],
        short_description:
          'Uncapped revenue capped at line item contract price (lifetime). Prevents overdelivery from exceeding contracted value',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.effective_lifetime_capped_actual_revenue': {
      sql: 'LEAST(line_item_metrics_daily.cumulative_uncapped_revenue, line_item_metrics_daily.line_items.price_value) * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Effective Lifetime Capped Revenue',
      format: 'currency',
      description:
        'Lifetime capped revenue after deducting referral commission. Requires line_item_id grouping.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'lifetime',
        type: 'effective_capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['line_item_metrics_daily.line_item_id'],
        formula: 'LEAST(cumulative_uncapped_revenue, price) * (1 - referral_commission_rate)',
        inputs: [
          'line_item_metrics_daily.cumulative_uncapped_revenue',
          'line_items.price_value',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Lifetime capped uncapped revenue after deducting referral commission',
        explanation:
          'Takes the lesser of cumulative uncapped revenue and the line item contract price, then applies the referral commission deduction. This is the most conservative revenue figure — capped at contract value and net of referral fees.',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.current_planned_revenue': {
      sql: 'SUM(CASE WHEN line_item_metrics_daily.day < CURRENT_DATE THEN line_item_metrics_daily.planned_revenue ELSE 0 END)',
      type: 'number',
      aggType: 'number',
      title: 'Planned Revenue (Current, Raw)',
      format: 'currency',
      description:
        'Cumulative planned revenue through yesterday (before referral commission). For rollups only - do not group by day.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        supports_timeseries: false,
        formula: 'SUM(planned_revenue) WHERE day < CURRENT_DATE',
        inputs: ['line_item_metrics_daily.planned_revenue'],
        short_description: 'Cumulative planned revenue through yesterday. Do not group by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.current_effective_planned_revenue': {
      sql: 'SUM(CASE WHEN line_item_metrics_daily.day < CURRENT_DATE THEN line_item_metrics_daily.planned_revenue * (1 - COALESCE(line_item_metrics_daily.line_items.referral_commission_rate, 0)) ELSE 0 END)',
      type: 'number',
      aggType: 'number',
      title: 'Effective Planned Revenue (Current)',
      format: 'currency',
      description:
        'Cumulative effective planned revenue through yesterday. For rollups only - do not group by day.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'effective',
        supports_rollup: true,
        supports_timeseries: false,
        formula: 'SUM(planned_revenue * (1 - referral_commission_rate)) WHERE day < CURRENT_DATE',
        inputs: ['line_item_metrics_daily.planned_revenue', 'line_items.referral_commission_rate'],
        short_description:
          'Cumulative effective planned revenue (after referral commission) through yesterday. Do not group by day',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.delivery_pacing': {
      sql: 'line_item_metrics_daily.actual_units / NULLIF(line_item_metrics_daily.planned_units, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Delivery Pacing',
      format: 'percent',
      description: 'Delivery pacing ratio. Actual units / planned units over specified date range',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_units / NULLIF(planned_units, 0)',
        inputs: ['line_item_metrics_daily.actual_units', 'line_item_metrics_daily.planned_units'],
        short_description:
          'Delivery pacing (actual_units / planned_units over specified date range)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.spend_pacing': {
      sql: 'line_item_metrics_daily.actual_spend / NULLIF(line_item_metrics_daily.planned_spend, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Spend Pacing',
      format: 'percent',
      description: 'Spend pacing ratio. Actual spend / planned spend over specified date range',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(planned_spend, 0)',
        inputs: ['line_item_metrics_daily.actual_spend', 'line_item_metrics_daily.planned_spend'],
        short_description: 'Spend pacing (actual_spend / planned_spend over specified date range)',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.margin_actual': {
      sql: '(line_item_metrics_daily.effective_billable_revenue - line_item_metrics_daily.media_cost) / NULLIF(line_item_metrics_daily.effective_billable_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Actual Margin',
      format: 'percent',
      description:
        'Actual margin percentage over specified date range. (Effective billable revenue - COGS) / Effective billable revenue',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: false,
        formula:
          '(effective_billable_revenue - media_cost) / NULLIF(effective_billable_revenue, 0)',
        inputs: [
          'line_item_metrics_daily.effective_billable_revenue',
          'line_item_metrics_daily.media_cost'
        ],
        short_description:
          'Actual margin percentage: (effective billable revenue - COGS) / effective billable revenue',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.margin_variance': {
      sql: '(line_item_metrics_daily.margin_actual - line_item_metrics_daily.planned_margin) / NULLIF(line_item_metrics_daily.planned_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Margin Variance',
      format: 'percent',
      description:
        'Variance between actual margin (over specified date range) and planned margin (from line item contract pricing)',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: '(margin_actual - planned_margin) / NULLIF(planned_margin, 0)',
        inputs: ['line_item_metrics_daily.margin_actual', 'line_item_metrics_daily.planned_margin'],
        short_description: 'Percentage variance between actual and planned margin',
        source_tables: ['metrics.line_item_metrics_daily', 'metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.unit_cost_actual': {
      sql: 'line_item_metrics_daily.actual_spend / NULLIF(line_item_metrics_daily.actual_units, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Actual Unit Cost',
      format: 'currency',
      description: 'Actual cost per unit delivered over specified date range. Spend / Units',
      meta: {
        category: 'cost',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(actual_units, 0)',
        inputs: ['line_item_metrics_daily.actual_spend', 'line_item_metrics_daily.actual_units'],
        short_description: 'Actual cost per unit delivered',
        source_tables: ['metrics.line_item_metrics_daily'],
        is_unit_rate: true
      }
    } as const satisfies CubeMeasureEntry,
    'line_item_metrics_daily.unit_cost_variance': {
      sql: '(line_item_metrics_daily.unit_cost_actual - line_item_metrics_daily.planned_unit_cost) / NULLIF(line_item_metrics_daily.planned_unit_cost, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Unit Cost Variance',
      format: 'percent',
      description:
        'Variance between actual unit cost (over specified date range) and planned unit cost (from line item contract pricing)',
      meta: {
        category: 'cost',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: '(unit_cost_actual - planned_unit_cost) / NULLIF(planned_unit_cost, 0)',
        inputs: [
          'line_item_metrics_daily.unit_cost_actual',
          'line_item_metrics_daily.planned_unit_cost'
        ],
        short_description: 'Percentage variance between actual and planned unit cost',
        source_tables: ['metrics.line_item_metrics_daily', 'metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'line_item_metrics_daily.id': {
      sql: "line_item_metrics_daily.line_item_id || '-' || line_item_metrics_daily.day::text",
      type: 'string',
      title: 'Id',
      description: 'Composite key: line_item_id + day',
      primaryKey: true,
      meta: {
        short_description:
          'Composite key (line_item_id + day) uniquely identifying each daily metric row.',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'line_item_metrics_daily.strategy_id': {
      sql: 'line_item_metrics_daily.line_items.strategy_id',
      type: 'string',
      title: 'Strategy ID',
      description: 'Associated strategy identifier (via line_items join)',
      meta: {
        short_description: 'Strategy ID from parent line item. Filter/group by strategy',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'line_item_metrics_daily.line_item_id': {
      sql: 'line_item_id',
      type: 'string',
      title: 'Line Item ID',
      description: 'Parent line item identifier',
      meta: {
        short_description: 'Parent line item identifier',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'line_item_metrics_daily.day': {
      sql: 'day',
      type: 'time',
      title: 'Day',
      description: 'Calendar date for this daily metric record',
      meta: {
        short_description: 'Calendar date for daily metric record',
        source_tables: ['metrics.line_item_metrics_daily']
      }
    } as const satisfies CubeDimensionEntry,
    'line_item_metrics_daily.planned_unit_cost': {
      sql: 'line_item_metrics_daily.line_items.planned_unit_cost',
      type: 'number',
      title: 'Planned Unit Cost',
      format: 'currency',
      description: 'Expected cost per unit based on media plan',
      meta: {
        category: 'cost',
        short_description: 'Expected cost per unit from the media plan',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeDimensionEntry,
    'line_item_metrics_daily.planned_margin': {
      sql: 'line_item_metrics_daily.line_items.planned_margin',
      type: 'number',
      title: 'Planned Margin',
      format: 'percent',
      description:
        'Expected margin percentage derived from line-item static pricing: (unit_price - planned_unit_cost) / unit_price. This is NOT derived from daily pacing data — it reflects the margin built into the contract terms at the time the line item was created. For custom-paced items, the daily pacing schedule may imply a different effective margin on any given day. Compare with margin_actual for realized performance.',
      meta: {
        category: 'margin',
        type: 'planned',
        formula: '(unit_price - planned_unit_cost) / unit_price',
        inputs: ['line_items.unit_price', 'line_items.planned_unit_cost'],
        short_description:
          'Expected margin from contract pricing: (unit_price - planned_unit_cost) / unit_price. Not from daily pacing',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry
  }
};
